import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { LuDownload, LuUpload } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";

const TransferSection = () => {
  const [importFile, setImportFile] = useState(null);

  const exportMutation = useMutation({
    mutationFn: () => API.get("/config-transfer/export"),
    onSuccess: ({ data }) => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `config-export-${data.sourceIndexName}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Export : ${data.questions.length} questions, ${data.guidelines.length} consignes, ${data.actions.length} actions`);
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de l'export de la configuration");
    },
  });

  const importMutation = useMutation({
    mutationFn: async (file) => API.post("/config-transfer/import", JSON.parse(await file.text())),
    onSuccess: ({ data }) => {
      toast.success(`Import : ${data.questionsImported} questions, ${data.guidelinesImported} consignes, ${data.actionsImported} actions`);
      data.warnings?.forEach((warning) => toast(warning, { icon: "⚠️" }));
      setImportFile(null);
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur lors de l'import de la configuration");
    },
  });

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Import / Export</h2>
        <p className="mt-1.5 text-sm text-gray-500">Transférez les questions, consignes et règles d'un client à un autre avec un fichier JSON.</p>
      </div>

      <div className="divide-y divide-gray-100 rounded-xl bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3.5">
          <div className="min-w-0 flex-1 basis-60">
            <p className="text-[13px] font-medium text-gray-900">Exporter la configuration</p>
            <p className="mt-0.5 text-[13px] text-gray-500">Télécharge les questions, consignes et règles de ce client.</p>
          </div>
          <button
            type="button"
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-white pl-2 pr-3 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LuDownload className="size-3.5" />
            {exportMutation.isPending ? "Export en cours..." : "Exporter"}
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3.5">
          <div className="min-w-0 flex-1 basis-60">
            <p className="text-[13px] font-medium text-gray-900">Importer une configuration</p>
            <p className="mt-0.5 text-[13px] text-gray-500">Les éléments importés s'ajoutent à la configuration existante, sans la remplacer.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className="flex h-8 max-w-56 cursor-pointer items-center gap-1.5 rounded-md bg-white pl-2 pr-3 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50">
              <LuUpload className="size-3.5 shrink-0" />
              <p className="truncate">{importFile ? importFile.name : "Choisir un fichier JSON"}</p>
              <input type="file" name="importFile" accept=".json" className="sr-only" onChange={(e) => setImportFile(e.target.files[0] || null)} />
            </label>
            <button
              type="button"
              disabled={!importFile || importMutation.isPending}
              onClick={() => importMutation.mutate(importFile)}
              className="h-8 rounded-md bg-primary-500 px-3 text-[13px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(0,0,0,0.12)] transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importMutation.isPending ? "Import en cours..." : "Importer"}
            </button>
          </div>
        </div>
      </div>

      <ul role="list" className="mt-2.5 space-y-1 px-4 text-xs text-gray-500">
        <li>Les références entre questions (déclencheurs) sont automatiquement remappées.</li>
        <li>Les déclencheurs de type laboratoire perdent leurs valeurs, car elles sont propres à chaque laboratoire.</li>
      </ul>
    </div>
  );
};

export default TransferSection;
